import styles from './TravelDateSelector.module.scss'
import ratioTravelDateObject from '@/entities/ratioTravelDate/interface'
import { dateRangeDays, isFutureDate, toUiDate } from '@/lib/helpers/helpers'
import CustomSelect from '@/components/elements/inputs/CustomSelect'
import { useTranslation } from 'next-i18next'
import { useContext } from 'react'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { getStateBooking, selectBookings, setTravelDate } from '@/features/booking'
import { TravelIdContext } from '@/app/context'
import LocalBooking from '@/lib/booking/LocalBooking'
import { bookingStateItem } from '@/features/booking/types'
import { AppConfigContext } from '@/app/providers/AppConfigProvider'
import FullyBookedOverlay from '@/components/models/booking/BookingWizard/components/FullyBookedOverlay'
import { CustomSelectOption } from '@/components/elements/inputs/CustomSelect/CustomSelect'

interface Props {
  colorTheme?: 'light' | 'dark'
}
const TravelDateSelector = ({ colorTheme = 'light' }: Props) => {
  const { t } = useTranslation('common')
  const appConfig = useContext(AppConfigContext)

  const travelId = useContext(TravelIdContext)
  const dispatch = useAppDispatch()
  const bookings = useAppSelector(selectBookings)
  const booking = getStateBooking(bookings, travelId) as bookingStateItem
  const manager = new LocalBooking(booking)

  const onChange = (date: CustomSelectOption<ratioTravelDateObject>) => {
    dispatch(setTravelDate({ travelId: travelId, date: date }))
  }

  const availableDates = manager.getAvailableTravelDates().map((date) => {
    // disable dates that are fully booked
    if (date.availabilityStatus && date.availabilityStatus.status === 'CLOSED') {
      return { ...date, disabled: true }
    }
    return date
  })
  const selectedDate = booking && booking.travelDate && isFutureDate(booking.travelDate.startDate) ? booking.travelDate : undefined

  const getTravelDateLabel = (date: ratioTravelDateObject) => {
    if (date.startDate == date.endDate) {
      return toUiDate(date.startDate)
    } else {
      return `${toUiDate(date.startDate)} - ${toUiDate(date.endDate)}`
    }
  }
  const getDaysCountLabel = (date: ratioTravelDateObject) => {
    if (appConfig.booking.displayDurationDays) {
      return <span style={{ fontSize: '0.7rem' }}>({t('days', { count: dateRangeDays(date.startDate, date.endDate) })})</span>
    }
    return <></>
  }
  return (
    <>
      {booking && (
        <CustomSelect
          variant={colorTheme === 'light' ? 'default' : 'whiteOutline'}
          placeholder={t(availableDates.length > 0 ? 'booking.travelDate' : 'booking.noAvailableTravelDate')}
          options={availableDates as CustomSelectOption<ratioTravelDateObject>[]}
          selected={selectedDate as CustomSelectOption<ratioTravelDateObject>}
          disabled={!booking.pricingTravel || !availableDates.length}
          onChange={onChange}
          renderOption={(date: CustomSelectOption<ratioTravelDateObject>) => {
            // @ts-ignore
            const isDisabled = date.disabled
            return (
              <div className={styles.option}>
                <FullyBookedOverlay contingent={isDisabled ? 0 : 1} />
                <div className={styles.date}>
                  {`${getTravelDateLabel(date)}`} {getDaysCountLabel(date)}
                </div>
                {date.includesExtension && (
                  <div className={styles.extension}>
                    {t('booking.with') + ' '}
                    <span className={styles.label}>{date.extension.label}</span>
                  </div>
                )}
              </div>
            )
          }}
          renderInputValue={(date: ratioTravelDateObject) => {
            return (
              <>
                {getTravelDateLabel(date)} {getDaysCountLabel(date)}
              </>
            )
          }}
        />
      )}
    </>
  )
}

export default TravelDateSelector
